import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import EntityTable from '../components/Listing/EntityTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faEllipsisH, faPen, faKey, faUserSlash, faUserCheck } from '@fortawesome/free-solid-svg-icons';
import InputText from '../components/Form/Fields/InputText';
import FabGroup from '../components/FabGroup';
import CreateUserModal from '../components/Modal/CreateUserModal';
import ConfirmModal from '../components/Modal/ConfirmModal';
import PatientImage from '../components/Patient/PatientImage';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';

export default function AdminUsers() {
    const { t } = useTranslation();
    const { setTitle, setTitleIcon } = usePageTitle();
    const { hasRight } = useAuth();
    const canCreate = hasRight('ADMIN_USERS_CREATE');
    const canEdit = hasRight('ADMIN_USERS_EDIT');
    const canActivate = hasRight('ADMIN_USERS_ACTIVATE');
    const tableRef = useRef(null);
    const [showModal, setShowModal] = useState(false);
    const [editEmployee, setEditEmployee] = useState(null);
    const [deactivateTarget, setDeactivateTarget] = useState(null);
    const [resetTarget, setResetTarget] = useState(null);
    const [sendingReset, setSendingReset] = useState(false);

    useEffect(() => {
        setTitle(`${t('Users')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('admin_users.subtitle')}</span>`);
        document.title = t('Users');
        setTitleIcon('ti-users');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useBreadcrumbSetup([
        { label: t('Administration') },
        { label: t('Users'), url: '/admin/users' },
    ]);

    const handleSaved = () => tableRef.current?.reload();
    const openCreate  = () => { setEditEmployee(null); setShowModal(true); };
    const openEdit    = (row) => { setEditEmployee(row); setShowModal(true); };

    const setActive = (row, active) => {
        axios.patch(`${API_BASE_URL}/api/v1/admin/users/${row.id}/active`, { active }, { withCredentials: true })
            .then(() => {
                toast.success(active ? t('admin_users.toast.activated') : t('admin_users.toast.deactivated'));
                tableRef.current?.reload();
            })
            .catch((err) => toast.error(err.response?.data?.message || t('admin_users.toast.generic_error')));
    };

    const confirmDeactivate = () => {
        setActive(deactivateTarget, false);
        setDeactivateTarget(null);
    };

    // Sent only once confirmed (resetTarget → ConfirmModal below)
    const confirmResetPassword = () => {
        const row = resetTarget;
        setSendingReset(true);
        axios.post(`${API_BASE_URL}/api/v1/admin/users/${row.id}/reset-password-request`, {}, { withCredentials: true })
            .then(() => toast.success(t('admin_users.toast.reset_sent', { email: row.email })))
            .catch(() => toast.error(t('admin_users.toast.reset_error')))
            .finally(() => {
                setSendingReset(false);
                setResetTarget(null);
            });
    };

    const columns = [
        {
            Header: '#',
            accessor: 'id',
            width: '1%',
            sortable: true,
            className: 'text-center',
            Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>{row.id}</span>,
        },
        {
            Header: t('admin_users.column.user'),
            accessor: 'firstName',
            // the only column that grows: the others shrink to their content (width 1% + nowrap)
            width: '100%',
            sortable: true,
            filterable: { component: InputText },
            Cell: (row) => (
                <div className="d-flex align-items-center gap-3">
                    <PatientImage patient={row} size={40} />
                    <div>
                        {canEdit ? (
                            // the name opens the edit modal (same as the "Modifier" action)
                            <button type="button" className="cell-name cell-link-btn" onClick={() => openEdit(row)}>
                                {row.firstName} {row.lastName}
                            </button>
                        ) : (
                            <span className="cell-name">{row.firstName} {row.lastName}</span>
                        )}
                        <span className="cell-sub">{row.email}</span>
                    </div>
                </div>
            ),
        },
        {
            Header: t('Roles'),
            accessor: 'roles',
            className: 'text-nowrap',
            width: '1%',
            Cell: (row) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(row.roles || []).map(r => (
                        <span key={r} style={{
                            fontSize: 11, fontWeight: 600,
                            background: '#e0e7ff', color: '#4338ca',
                            border: '1px solid #c7d2fe',
                            borderRadius: 20, padding: '2px 10px',
                        }}>{r}</span>
                    ))}
                </div>
            ),
        },
        {
            Header: t('Status'),
            accessor: 'active',
            className: 'text-center text-nowrap',
            width: '1%',
            Cell: (row) => row.active
                ? <span className="badge bg-success">{t('admin_users.status.active')}</span>
                : <span className="badge bg-secondary">{t('admin_users.status.inactive')}</span>,
        },
        {
            Header: '',
            accessor: 'actions',
            className: 'col-actions',
            width: '1%',
            Cell: (row) => (
                <div className="cell-dropdown">
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => openEdit(row)}>
                                    <FontAwesomeIcon icon={faPen} className="me-2 text-muted" />
                                    {t('Edit')}
                                </button>
                            </li>
                        )}
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => setResetTarget(row)}>
                                    <FontAwesomeIcon icon={faKey} className="me-2 text-muted" />
                                    {t('admin_users.action.reset_password')}
                                </button>
                            </li>
                        )}
                        {canActivate && <li><hr className="dropdown-divider" /></li>}
                        {canActivate && (
                            <li>
                                {row.active
                                    ? (
                                        <button className="dropdown-item danger" onClick={() => setDeactivateTarget(row)}>
                                            <FontAwesomeIcon icon={faUserSlash} className="me-2" />
                                            {t('admin_users.action.deactivate')}
                                        </button>
                                    ) : (
                                        <button className="dropdown-item" onClick={() => setActive(row, true)}>
                                            <FontAwesomeIcon icon={faUserCheck} className="me-2 text-muted" />
                                            {t('admin_users.action.activate')}
                                        </button>
                                    )
                                }
                            </li>
                        )}
                    </ul>
                </div>
            ),
        },
    ];

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
    // — reuses the actions column's own dropdown so both views offer identical actions.
    const actionsColumn = columns.find((c) => c.accessor === 'actions');
    const renderUserCard = (row) => (
        <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                {row.active
                    ? <span className="badge bg-success" style={{ alignSelf: 'flex-start' }}>{t('admin_users.status.active')}</span>
                    : <span className="badge bg-secondary" style={{ alignSelf: 'flex-start' }}>{t('admin_users.status.inactive')}</span>}

                <div className="d-flex align-items-center gap-2" style={{ marginTop: 4 }}>
                    <PatientImage patient={row} size={36} />
                    <div style={{ minWidth: 0 }}>
                        {canEdit ? (
                            <button
                                type="button"
                                className="cell-link-btn"
                                style={{ fontSize: 13.5, fontWeight: 600, color: '#111827', display: 'block' }}
                                onClick={() => openEdit(row)}
                            >
                                {row.firstName} {row.lastName}
                            </button>
                        ) : (
                            <span style={{ fontSize: 13.5, fontWeight: 600, color: '#111827', display: 'block' }}>
                                {row.firstName} {row.lastName}
                            </span>
                        )}
                        <span style={{ fontSize: 11.5, color: '#9ca3af', display: 'block' }}>{row.email}</span>
                    </div>
                </div>

                {(row.roles || []).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {row.roles.map(r => (
                            <span key={r} style={{
                                fontSize: 11, fontWeight: 600,
                                background: '#e0e7ff', color: '#4338ca',
                                border: '1px solid #c7d2fe',
                                borderRadius: 20, padding: '2px 10px',
                            }}>{r}</span>
                        ))}
                    </div>
                )}

                <div className="d-flex align-items-center justify-content-end" style={{ marginTop: 'auto', paddingTop: 6 }}>
                    {actionsColumn?.Cell(row)}
                </div>
            </div>
        </div>
    );

    const buttons = canCreate
        ? [{ label: t('admin_users.new_user'), className: 'btn-primary', icon: faCirclePlus, onClick: openCreate }]
        : [];

    return (
        <>
            <EntityTable
                ref={tableRef}
                endpoint={`${API_BASE_URL}/api/v1/admin/users`}
                columns={columns}
                defaultSort="firstName"
                defaultDirection="asc"
                pageSize={20}
                emptyMessage={t('admin_users.empty')}
                searchable={true}
                searchPlaceholder={t('admin_users.search_placeholder')}
                enableCardView={true}
                renderCard={renderUserCard}
            />
            <FabGroup buttons={buttons} />

            <CreateUserModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onSaved={handleSaved}
                employee={editEmployee}
            />

            <ConfirmModal
                show={!!deactivateTarget}
                title={t('admin_users.deactivate_title')}
                description={deactivateTarget ? t('admin_users.deactivate_confirm', { name: `${deactivateTarget.firstName} ${deactivateTarget.lastName}` }) : ''}
                confirmLabel={t('admin_users.action.deactivate_confirm_label')}
                confirmVariant="danger"
                onClose={() => setDeactivateTarget(null)}
                onConfirm={confirmDeactivate}
            />

            <ConfirmModal
                show={!!resetTarget}
                title={t('admin_users.reset_title')}
                description={resetTarget ? t('admin_users.reset_confirm', { name: `${resetTarget.firstName} ${resetTarget.lastName}`, email: resetTarget.email }) : ''}
                confirmLabel={t('admin_users.action.reset_confirm_label')}
                confirmVariant="primary"
                icon={faKey}
                isLoading={sendingReset}
                onClose={() => setResetTarget(null)}
                onConfirm={confirmResetPassword}
            />
        </>
    );
}
